/**
 * EventsTimelineChart - Collapsible line chart showing active events over time.
 * Extracted from PopupEventsPage for readability.
 */
import React from 'react';
import {
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@ais-ui/ui-core';
import { ChevronUp, ChevronDown } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

export interface TimelineDataPoint {
  time: string;
  fullTime: string;
  active: number;
}

interface EventsTimelineChartProps {
  data: TimelineDataPoint[];
  isMinimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  formatDateTime: (
    date: Date | null | undefined,
    hideSeconds?: boolean,
  ) => string;
  messages: Record<string, string>;
}

export const EventsTimelineChart: React.FC<EventsTimelineChartProps> = ({
  data,
  isMinimized,
  onMinimizedChange,
  formatDateTime,
  messages,
}) => {
  return (
    <Collapsible
      open={!isMinimized}
      onOpenChange={(open) => onMinimizedChange(!open)}
    >
      <Card className="p-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium">
            {messages.popupEventsActiveOverTime}
          </h3>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              {isMinimized ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={data}
              margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                tickCount={6}
              />
              <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const item = payload[0].payload as {
                    fullTime: string;
                    active: number;
                  };
                  return (
                    <div className="bg-background border rounded px-2 py-1 shadow-sm">
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(new Date(item.fullTime))}
                      </p>
                      <p className="text-sm font-medium">
                        {item.active} active events
                      </p>
                    </div>
                  );
                }}
              />
              <Line
                type="stepAfter"
                dataKey="active"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                name="Active Events"
              />
            </LineChart>
          </ResponsiveContainer>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
